import React, { useCallback, useState } from 'react'

import { getElement } from 'components/Editor/EditorContext/selectors/block'
import EditorElementComponent from 'components/Editor/EditorElements/EditorElementComponent'
import ControlSelection from 'components/Editor/cource/layout/controls/ControlSelection'
import EditorCommentBubble from 'components/comments/EditorCommentBubble'
import EditorCreateComment from 'components/comments/EditorCreateComment'
import { EMPTY_OBJECT } from 'constants/commonConstans'
import { scrollProps } from 'services/Scroll/ScrollService'
import { setCommentForm } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getIsCommentedElement,
  getIsCommenting,
  getIsOpenComment,
  getNodeStyle,
} from 'services/Store/Project/selectors'

import type { INodeV2Props } from '../../cource/layout/Node/types'
import { useNodeAnimation } from '../formControls/Animation/useNodeAnimation'
import { getNodeLabel } from '../getNodeLabel'
import { isRoot } from '../guards'
import NodeV2Styled from '../styled/NodeV2Styled'

const PreviewNodeV2: React.FC<INodeV2Props> = ({ children, node, block, mode, parent, isSlot }) => {
  const dispatch = useProjectDispatch()
  const { uuid: blockId } = block
  const { id: nodeId, elementId } = node
  const element = node.elementId ? getElement(block, node.elementId) : null
  const style = useProjectContext(getNodeStyle, block.uuid, node.id)
  const parentStyle = useProjectContext(getNodeStyle, block.uuid, parent?.id)
  const [highlight, setHighlight] = useState(false)
  const isOpenedComment = useProjectContext(getIsOpenComment, { blockId, nodeId, elementId })
  const label = getNodeLabel(block, node, style)
  const isCommented = useProjectContext(getIsCommentedElement, elementId)
  const isCommenting = useProjectContext(getIsCommenting, blockId, elementId)
  const onMouseEnter = () => setHighlight(true)
  const onMouseLeave = () => setHighlight(false)
  const { onClick, ...animation } = useNodeAnimation(node) || EMPTY_OBJECT

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (elementId && mode.editorMode === AppMode.comment) {
        e.stopPropagation()
        dispatch(setCommentForm({ blockId: blockId, elementId: elementId }))
      }
    },
    [dispatch, blockId, mode.editorMode, elementId],
  )

  return (
    <NodeV2Styled
      {...scrollProps(nodeId)}
      {...animation}
      $element={element}
      $isRoot={isRoot(block, node)}
      $isSlot={isSlot}
      $node={node}
      $parent={parent}
      $parentStyle={parentStyle}
      $style={style}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {element ? (
        <>
          <EditorElementComponent block={block} element={element} mode={mode} isV2 />

          {isCommenting && <EditorCreateComment blockId={block.uuid} elementId={elementId} />}
          {isCommented && (
            <EditorCommentBubble blockId={blockId} elementId={element.id} nodeId={nodeId} />
          )}

          {((highlight && mode.editorMode === AppMode.comment) ||
            isCommenting ||
            isOpenedComment) && (
            <ControlSelection
              active={false}
              highlighted={highlight || isOpenedComment || isCommenting}
              label={label}
              name='shellSelection'
            />
          )}
        </>
      ) : (
        children
      )}
    </NodeV2Styled>
  )
}

export default React.memo(PreviewNodeV2)
